import { useState } from "react";
import ChartCard from "../../../../shared/charts/ChartCard";
import apiClient from "../../../../services/api";

/**
 * codex.md v4 — editor for the monthly P&L series that the new Revenue /
 * Cost / Profit charts read from. Covers:
 *   - Revenue Paid / Pending  (tracker-owned; CFO edits only for gaps
 *     like January under postpaid billing)
 *   - Revenue Expected / Forecast / Cost Actual / Cost Expected /
 *     Cost Forecast  (CFO manual always)
 *
 * Also exposes the ⟳ Sync Invoices trigger (CFO+ only) next to the
 * Edit Monthly Values toggle, matching the pre-v4 UX.
 *
 * Props:
 *   monthlyFin: API response from /financial-status/monthly (array).
 *   reloadMonthlyFin: re-fetches the page-level monthlyFin state.
 *   showToast, canSee, year.
 */
const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const EDITABLE_FIELDS = [
  { key: "revenueActual",        label: "Rev Paid ⟳",    tooltip: "Tracker writes this on sync (paid + partially_paid). Editable for postpaid gaps; next sync may overwrite." },
  { key: "revenueActualPending", label: "Rev Pending ⟳", tooltip: "Tracker writes this on sync (sent / viewed). Editable for postpaid gaps; next sync may overwrite." },
  { key: "revenueExpected",      label: "Rev Expected",  tooltip: "CFO target for the month." },
  { key: "revenueForecast",      label: "Rev Forecast",  tooltip: "CFO projection." },
  { key: "costActual",           label: "Cost Actual",   tooltip: "CFO-entered realised cost (full OpEx)." },
  { key: "costExpected",         label: "Cost Expected", tooltip: "CFO plan." },
  { key: "costForecast",         label: "Cost Forecast", tooltip: "CFO forward projection." },
];

function byMonth(arr) {
  const m = new Map();
  if (Array.isArray(arr)) for (const r of arr) if (r?.month) m.set(r.month, r);
  return m;
}

function valueAt(row, field) {
  if (!row) return "";
  // revenue fields live on row.revenue.*; cost fields on row.cost.*.
  if (field.startsWith("revenue")) {
    const sub = field === "revenueActual" ? "actual"
              : field === "revenueActualPending" ? "actualPending"
              : field.replace("revenue", "").toLowerCase();
    return row.revenue?.[sub] ?? "";
  }
  if (field.startsWith("cost")) {
    const sub = field.replace("cost", "").toLowerCase();
    return row.cost?.[sub] ?? "";
  }
  return "";
}

export default function MonthlyFinancialsEditor({ monthlyFin, reloadMonthlyFin, canSee, showToast, year }) {
  const [editOpen, setEditOpen] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [savingKey, setSavingKey] = useState(null);
  if (!canSee) return null;

  const data = Array.isArray(monthlyFin) ? monthlyFin : [];
  const map = byMonth(data);

  async function saveCell(month, field, raw) {
    const key = `${month}|${field}`;
    setSavingKey(key);
    try {
      const v = raw === "" || raw === null || raw === undefined ? null : Number(raw);
      if (v !== null && !Number.isFinite(v)) throw new Error("not a number");
      await apiClient.updateMonthlyFinancials({ year, month, [field]: v });
      await reloadMonthlyFin();
    } catch (err) {
      showToast?.("Error", err?.response?.data?.error || "Failed to save");
    } finally {
      setSavingKey(null);
    }
  }

  async function runInvoiceSync() {
    if (syncing) return;
    setSyncing(true);
    try {
      const r = await apiClient.syncInvoicesNow();
      showToast?.("Invoice sync", `${r?.months ?? 0} months · ${r?.updated ?? 0} updated · ${r?.created ?? 0} created`);
      await reloadMonthlyFin();
    } catch (err) {
      showToast?.("Error", err?.response?.data?.error || "Sync failed");
    } finally {
      setSyncing(false);
    }
  }

  const right = (
    <div style={{ display: "flex", gap: 8 }}>
      <button onClick={runInvoiceSync} disabled={syncing}
        style={btnSecondary(syncing)}
        title="Pull paid + partially-paid invoices from Tracker now. Daily cron handles the rest.">
        {syncing ? "⟳ Syncing…" : "⟳ Sync Invoices"}
      </button>
      <button onClick={() => setEditOpen((v) => !v)} style={btnPrimary(editOpen)}>
        {editOpen ? "✕ Close Editor" : "✎ Edit Monthly Values"}
      </button>
    </div>
  );

  return (
    <ChartCard
      title={`Monthly P&L editor — ${year}`}
      subtitle="Revenue Paid + Pending come from Tracker invoices (editable for postpaid gaps). Expected, Forecast, and every Cost cell are CFO-manual. Profit is derived."
      right={right}
      minHeight={editOpen ? 360 : 120}
    >
      {!editOpen ? (
        <div style={{ fontSize: 12, color: "#8fa4c8" }}>
          Click <strong>✎ Edit Monthly Values</strong> to reconcile months that tracker can't cover (January under postpaid billing), or add expected / forecast targets.
        </div>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11, fontFamily: "'Noto Sans', sans-serif" }}>
            <thead>
              <tr style={{ color: "#6889b4", textAlign: "left" }}>
                <th style={th}>Month</th>
                {EDITABLE_FIELDS.map((f) => (
                  <th key={f.key} style={th} title={f.tooltip}>{f.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {MONTHS.map((m, i) => {
                const month = i + 1;
                const row = map.get(month);
                return (
                  <tr key={m} style={{ borderBottom: "1px solid #0a1628" }}>
                    <td style={{ padding: "5px 4px", color: "#8fa4c8", fontWeight: 600 }}>{m}</td>
                    {EDITABLE_FIELDS.map((f) => {
                      const current = valueAt(row, f.key);
                      const saving = savingKey === `${month}|${f.key}`;
                      return (
                        <td key={f.key} style={{ padding: "5px 4px" }}>
                          <input
                            type="number"
                            defaultValue={current === null || current === undefined ? "" : Math.round(current)}
                            disabled={saving}
                            onBlur={(e) => {
                              const nextRaw = e.target.value;
                              const next = nextRaw === "" ? null : Number(nextRaw);
                              if (next === (current ?? null)) return;
                              saveCell(month, f.key, nextRaw);
                            }}
                            style={inp}
                          />
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </ChartCard>
  );
}

// Inline styles — keeping the file self-contained so reuse is easy and
// legacy pages aren't affected by Tailwind utility bleed.
const inp = {
  width: 96,
  padding: "4px 6px",
  background: "#070e20",
  border: "1px solid #14213d",
  borderRadius: 4,
  color: "#e2e8f6",
  fontSize: 11,
};
const th = { padding: "6px 4px", borderBottom: "1px solid #14213d", whiteSpace: "nowrap" };
function btnSecondary(busy) {
  return {
    padding: "6px 12px",
    borderRadius: 6,
    border: "1px solid #14213d",
    background: busy ? "#0f1e3a" : "#070e20",
    color: busy ? "#6889b4" : "#8fa4c8",
    cursor: busy ? "wait" : "pointer",
    fontSize: 11,
    fontWeight: 600,
    whiteSpace: "nowrap",
  };
}
function btnPrimary(active) {
  return {
    padding: "6px 12px",
    borderRadius: 6,
    border: `1px solid ${active ? "#3b7ffa" : "#14213d"}`,
    background: active ? "rgba(59,127,250,.14)" : "#070e20",
    color: active ? "#60a5fa" : "#8fa4c8",
    cursor: "pointer",
    fontSize: 11,
    fontWeight: 600,
    whiteSpace: "nowrap",
  };
}
